import React from 'react'
import classnames from 'classnames/bind'
import type {
  WorkflowResource,
  ShallowTaskWorkflowGroup,
  ShallowTaskWorkflowItemStatus,
  ShallowTaskWorkflowMilestone,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import { useTransition, animated } from 'react-spring'
import Icon from '../elementals/icon'
import Text from '../elementals/text'
import InfoPill from '../elementals/infoPill'
import LabeledProgressBar from '../elementals/labeledProgressBar'
import { ColorNames } from '../../constants'
import { formatDuration, computeElapsed } from '../../utils/formatters'
import MilestoneLabel from './MilestoneLabel'
import MilestoneDate from './MilestoneDate'
import MilestoneAction from './milestoneAction'
import InstructionsBanner from './InstructionsBanner'
import styles from './styles.scss'
import { PopupTooltip } from '../overlays'
import type { ShallowTaskWorkflowProps } from '.'

const cx = classnames.bind(styles)

type LocalIconProps = {
  color?: 'success' | 'grayMediumLight' | 'grayDark' | string
  iconStyle?: 'solid' | 'regular' | 'light' | 'brand' | undefined
}

const milestoneIconStyles: Record<
  ShallowTaskWorkflowItemStatus,
  LocalIconProps
> = {
  [graph.enums.ShallowTaskWorkflowItemStatus.Complete]: {
    color: 'success',
    iconStyle: 'solid',
  },
  [graph.enums.ShallowTaskWorkflowItemStatus.Ongoing]: {
    color: 'success',
    iconStyle: 'regular',
  },
  [graph.enums.ShallowTaskWorkflowItemStatus.Upcoming]: {
    color: 'grayMediumLight',
    iconStyle: 'regular',
  },
}

const milestoneTextStyles = {
  [graph.enums.ShallowTaskWorkflowItemStatus.Complete]: { color: 'success' },
  [graph.enums.ShallowTaskWorkflowItemStatus.Ongoing]: { color: 'success' },
  [graph.enums.ShallowTaskWorkflowItemStatus.Upcoming]: { color: 'grayDark' },
}

/** Map result_type to DS ColorNames for consistent theming */
const RESULT_TYPE_COLORS: Record<string, ColorNames> = {
  success: ColorNames.Success,
  error: ColorNames.Caution,
  warning: ColorNames.CautionActiveMedium,
  info: ColorNames.Gray,
}

/**
 * Extended milestone type that supports pipeline progress fields.
 * These fields are optional and do not exist in the base SDK type.
 */
export interface ExtendedShallowTaskWorkflowMilestone
  extends ShallowTaskWorkflowMilestone {
  /** Result/detail text shown when step is complete */
  detail?: string
  /** Rendering style: 'text' (default) or 'badge' (rendered as InfoPill) */
  detail_variant?: 'text' | 'badge'
  /** Controls detail text color via DS ColorNames */
  result_type?: 'success' | 'error' | 'warning' | 'info'
  /** Current progress count for sub-progress display */
  processed_items?: number
  /** Total items for sub-progress display */
  total_items?: number
  /** ISO timestamp when step began processing */
  started_at?: string
  /** Signals this step can block for user input */
  requires_user_input?: boolean
}

/**
 * Extended group type that supports nested sub-groups and pipeline features.
 * A group can contain either `tasks` (leaf milestones) or `groups` (nested sub-groups),
 * or both — though typically it's one or the other.
 */
export interface ExtendedShallowTaskWorkflowGroup
  extends ShallowTaskWorkflowGroup {
  tasks: ExtendedShallowTaskWorkflowMilestone[]
  groups?: ExtendedShallowTaskWorkflowGroup[]
  /** When true, render a progress bar between description and content */
  include_progress_bar?: boolean
  /** When true, display dates with time (down to seconds) instead of default M/D/YY */
  show_timestamp?: boolean
  /** When true, enable entrance animations for dynamically added groups/steps */
  animated?: boolean
  /** When true, pulse ONGOING status icons */
  pulse?: boolean
  /** When true, show sub-progress (processed_items/total_items) on ONGOING tasks */
  show_sub_progress?: boolean
  /** When true, show time-only label with full date+time in tooltip on hover */
  compact_time?: boolean
}

// --- MilestoneRow Component ---

interface MilestoneRowProps<T extends WorkflowResource = WorkflowResource> {
  task: ExtendedShallowTaskWorkflowMilestone
  taskElmId: string
  resource: T
  slots?: ShallowTaskWorkflowProps<T>['slots']
  iconSize: 'medium' | 'large'
  compact?: boolean
  isDisabled?: boolean
  isPulse: boolean
  showSubProgress: boolean
  showTimestamp?: boolean
  compactTime?: boolean
  workflowStatus: string
}

/**
 * Renders a single milestone/step row.
 * Builds default pieces, then defers to slots for customization.
 */
function MilestoneRow<T extends WorkflowResource = WorkflowResource>({
  task,
  taskElmId,
  resource,
  slots,
  iconSize,
  compact,
  isDisabled,
  isPulse,
  showSubProgress,
  showTimestamp,
  compactTime,
  workflowStatus,
}: MilestoneRowProps<T>) {
  const isOngoing =
    task.status === graph.enums.ShallowTaskWorkflowItemStatus.Ongoing
  const isComplete =
    task.status === graph.enums.ShallowTaskWorkflowItemStatus.Complete
  const taskElapsed = computeElapsed(task.started_at, task.completed_date)

  // --- Build default pieces ---

  const defaultLabel = (
    <MilestoneLabel
      elmId={`${taskElmId}_label`}
      milestone={task}
      workflowStatus={workflowStatus}
      compact={compact}
    />
  )

  const defaultSubProgress =
    showSubProgress &&
    isOngoing &&
    task.processed_items != null &&
    task.total_items != null &&
    task.total_items > 0 ? (
      <div className={cx('sub-progress')}>
        <LabeledProgressBar
          label=''
          color='cautionLight'
          count={task.processed_items}
          totalCount={task.total_items}
          unitMode='countAsFraction'
          height={4}
          rounded
        />
      </div>
    ) : null

  const defaultDetail = task.detail ? (
    <div className={cx('milestone-detail-container')}>
      {task.detail_variant === 'badge' ? (
        <InfoPill
          content={task.detail}
          backgroundColor={ColorNames.ActionExtraLight}
          foregroundColor={ColorNames.Action}
          includeBorder
          size='small'
          elmId={`${taskElmId}_detail_badge`}
        />
      ) : (
        <Text
          size='tiny'
          color={
            RESULT_TYPE_COLORS[task.result_type || 'info'] || ColorNames.Gray
          }
          className={cx('milestone-detail', {
            'milestone-detail-error': task.result_type === 'error',
          })}
        >
          {task.detail}
        </Text>
      )}
      {taskElapsed !== null && (
        <Text
          size='tiny'
          color={ColorNames.GrayMedium}
          className={cx('milestone-duration')}
        >
          {formatDuration(taskElapsed)}
        </Text>
      )}
    </div>
  ) : null

  const defaultDuration =
    !task.detail && taskElapsed !== null && isComplete ? (
      <Text
        size='tiny'
        color={ColorNames.GrayMedium}
        className={cx('milestone-duration')}
      >
        {formatDuration(taskElapsed)}
      </Text>
    ) : null

  const defaultProcessing =
    isOngoing &&
    !task.processed_items &&
    !task.detail &&
    !task.requires_user_input ? (
      <Text
        size='tiny'
        color={ColorNames.CautionActiveMedium}
        weight='heavy'
        className={cx('milestone-detail-processing')}
      >
        Processing...
      </Text>
    ) : null

  const defaultDate = (
    <MilestoneDate
      elmId={`${taskElmId}_date`}
      milestone={task}
      compact={compact}
      showTimestamp={showTimestamp}
      compactTime={compactTime}
    />
  )

  const defaultAction = (
    <MilestoneAction
      milestone={task}
      resource={resource}
      isDisabled={isDisabled}
    />
  )

  const defaultTaskActions = slots?.taskActions ? (
    <div>{slots.taskActions({ task, resource, isDisabled, compact })}</div>
  ) : null

  // --- Render with slot overrides ---

  return (
    <>
      {/* LEFT: Icon */}
      {slots?.taskIcon ? (
        <>
          {slots.taskIcon({
            task,
            resource,
            compact,
            default: (
              <Icon
                elmId={`${taskElmId}_icon`}
                name='check-circle'
                size={iconSize}
                className={cx({ pulse: isPulse && isOngoing })}
                {...(task.status && milestoneIconStyles[task.status])}
              />
            ),
          })}
        </>
      ) : (
        <Icon
          elmId={`${taskElmId}_icon`}
          name='check-circle'
          size={iconSize}
          className={cx({ pulse: isPulse && isOngoing })}
          {...(task.status && milestoneIconStyles[task.status])}
        />
      )}

      {/* CENTER: Content */}
      {slots?.milestoneContent ? (
        slots.milestoneContent({
          task,
          resource,
          defaults: {
            label: defaultLabel,
            subProgress: defaultSubProgress,
            detail: defaultDetail,
            duration: defaultDuration,
            processing: defaultProcessing,
          },
          compact,
        })
      ) : (
        <div className={cx('step-content')}>
          {defaultLabel}
          {defaultSubProgress}
          {defaultDetail}
          {defaultDuration}
          {defaultProcessing}
        </div>
      )}

      {/* RIGHT: Date + Actions */}
      {slots?.milestoneRight ? (
        slots.milestoneRight({
          task,
          resource,
          defaults: {
            date: defaultDate,
            action: defaultAction,
            taskActions: defaultTaskActions,
          },
          compact,
        })
      ) : (
        <div className={cx('milestone-date-action')}>
          {defaultDate}
          {defaultAction}
          {defaultTaskActions}
        </div>
      )}
    </>
  )
}

// --- WorkflowDetails Component ---

interface WorkGroupDetailsProps<T extends WorkflowResource = WorkflowResource> {
  group: ShallowTaskWorkflowGroup | ExtendedShallowTaskWorkflowGroup
  workflowStatus: string
  resource: T
  slots?: ShallowTaskWorkflowProps<T>['slots']
  elmId?: string
  isDisabled?: boolean
  compact?: boolean
  featureFlags?: Record<string, boolean>
  /** Internal: nesting depth for sub-groups (controls indent & sizing) */
  _depth?: number
}

function WorkflowDetails<T extends WorkflowResource = WorkflowResource>({
  group,
  workflowStatus,
  resource,
  slots,
  elmId = 'workflow_details',
  isDisabled,
  compact,
  featureFlags = {},
  _depth = 0,
}: WorkGroupDetailsProps<T>) {
  const groupStatusElmId = `${elmId}_${group.code}`.toLowerCase()
  const isNested = _depth > 0
  const iconSize = (compact || isNested ? 'medium' : 'large') as
    | 'medium'
    | 'large'
  const textSize = compact || isNested ? 'small' : 'large'

  const extendedGroup = group as ExtendedShallowTaskWorkflowGroup
  const hasSubGroups = extendedGroup.groups && extendedGroup.groups.length > 0
  const tasks = (extendedGroup.tasks ||
    []) as ExtendedShallowTaskWorkflowMilestone[]
  const isAnimated = extendedGroup.animated ?? false
  const isPulse = extendedGroup.pulse ?? false
  const showSubProgress = extendedGroup.show_sub_progress ?? false

  // Compute progress for optional progress bar
  const progressPercent = (() => {
    if (!extendedGroup.include_progress_bar) return 0
    const countTasks = (
      g: ExtendedShallowTaskWorkflowGroup
    ): { completed: number; total: number } => {
      let completed = 0
      let total = 0
      if (g.tasks?.length) {
        total += g.tasks.length
        completed += g.tasks.filter(t => t.status === 'COMPLETE').length
      }
      if (g.groups?.length) {
        for (const sub of g.groups) {
          const r = countTasks(sub)
          completed += r.completed
          total += r.total
        }
      }
      return { completed, total }
    }
    const { completed, total } = countTasks(extendedGroup)
    return total === 0 ? 0 : Math.round((completed / total) * 100)
  })()

  // Compute phase total duration when all tasks are complete
  const phaseDuration = (() => {
    if (
      group.status !== graph.enums.ShallowTaskWorkflowItemStatus.Complete ||
      !tasks.length
    )
      return null
    let totalMs = 0
    let allHaveTiming = true
    for (const task of tasks) {
      const elapsed = computeElapsed(task.started_at, task.completed_date)
      if (elapsed === null) {
        allHaveTiming = false
        break
      }
      totalMs += elapsed
    }
    return allHaveTiming && totalMs > 0 ? formatDuration(totalMs) : null
  })()

  // Animated transitions for tasks entering
  const taskTransitions = useTransition(tasks, {
    keys: (task: ExtendedShallowTaskWorkflowMilestone) => task.code,
    from: isAnimated
      ? { opacity: 0, transform: 'translateY(8px)' }
      : { opacity: 1, transform: 'translateY(0px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    config: { duration: 300 },
  })

  // Find task requiring user input that is currently blocking
  const blockingTask = tasks.find(
    t => t.requires_user_input && t.status === 'ONGOING'
  )

  return (
    <div className={cx('group-details', { compact, nested: isNested })}>
      <div className={cx('group-header', group.status.toLowerCase())}>
        <Icon
          name='check-circle'
          size={iconSize}
          elmId={`${groupStatusElmId}_icon`}
          className={cx({
            pulse:
              isPulse &&
              group.status ===
                graph.enums.ShallowTaskWorkflowItemStatus.Ongoing,
          })}
          {...milestoneIconStyles[group.status]}
        />
        <Text
          size={textSize}
          weight='black'
          elmId={`${groupStatusElmId}_label`}
          {...milestoneTextStyles[group.status]}
        >
          {group.label}
        </Text>
        {phaseDuration && (
          <Text
            size='tiny'
            color={ColorNames.GrayMedium}
            className={cx('phase-duration')}
          >
            ({phaseDuration})
          </Text>
        )}
        {group.label_tooltip && (
          // @ts-ignore next
          <PopupTooltip
            hideOnScroll
            trigger={<Icon name='info-circle' iconStyle='light' size='small' />}
            content={
              <Text size='tiny' color='white'>
                {group.label_tooltip}
              </Text>
            }
            position='top center'
            on='hover'
          />
        )}
      </div>

      <div
        className={cx('milestones-list', {
          complete:
            group.status === graph.enums.ShallowTaskWorkflowItemStatus.Complete,
        })}
      >
        {group.description ? (
          <div
            className={cx(compact ? 'u-mb-2' : 'u-mb-3')}
            data-elm-id={`${elmId}_group_description`}
          >
            <Text color='gray' size='tiny'>
              {group.description}{' '}
              {group.description_tooltip || group.tooltip_label ? (
                // @ts-ignore next
                <PopupTooltip
                  hideOnScroll
                  trigger={
                    <Icon name='info-circle' iconStyle='light' size='small' />
                  }
                  content={
                    <Text size='tiny' color='white'>
                      {group.description_tooltip || group.tooltip_label}
                    </Text>
                  }
                  position='top center'
                  on='hover'
                />
              ) : null}
            </Text>
          </div>
        ) : null}

        {/* Optional progress bar — driven by group data */}
        {extendedGroup.include_progress_bar && (
          <div className={cx(compact ? 'u-mb-2' : 'u-mb-3')}>
            <LabeledProgressBar
              label='Progress'
              color={progressPercent === 100 ? 'success' : 'cautionLight'}
              percent={progressPercent}
              rounded
            />
          </div>
        )}

        {/* Render nested sub-groups recursively */}
        {hasSubGroups &&
          extendedGroup.groups!.map(subGroup => (
            <WorkflowDetails
              key={subGroup.code}
              group={subGroup}
              workflowStatus={workflowStatus}
              resource={resource}
              slots={slots}
              elmId={`${elmId}_${subGroup.code}`.toLowerCase()}
              isDisabled={isDisabled}
              compact={compact}
              featureFlags={featureFlags}
              _depth={_depth + 1}
            />
          ))}

        {/* Render leaf tasks — with optional animation */}
        {isAnimated
          ? taskTransitions((style, task) => {
              const taskElmId = `${elmId}_${task.code}`.toLowerCase()
              return (
                <animated.div key={task.code} style={style}>
                  <div className={cx('milestone')}>
                    <MilestoneRow
                      task={task}
                      taskElmId={taskElmId}
                      resource={resource}
                      slots={slots}
                      iconSize={iconSize}
                      compact={compact}
                      isDisabled={isDisabled}
                      isPulse={isPulse}
                      showSubProgress={showSubProgress}
                      showTimestamp={extendedGroup.show_timestamp}
                      compactTime={extendedGroup.compact_time}
                      workflowStatus={workflowStatus}
                    />
                  </div>
                  {featureFlags[
                    'fe.resi.buyer_dashboard.enable_emd_instructions_banner'
                  ] && (
                    <InstructionsBanner
                      task={task}
                      resource={resource}
                      instructionActionSlot={slots?.instructionAction ?? null}
                    />
                  )}
                </animated.div>
              )
            })
          : tasks.map(task => {
              const taskElmId = `${elmId}_${task.code}`.toLowerCase()
              return (
                <React.Fragment key={task.code}>
                  <div className={cx('milestone')}>
                    <MilestoneRow
                      task={task}
                      taskElmId={taskElmId}
                      resource={resource}
                      slots={slots}
                      iconSize={iconSize}
                      compact={compact}
                      isDisabled={isDisabled}
                      isPulse={isPulse}
                      showSubProgress={showSubProgress}
                      showTimestamp={extendedGroup.show_timestamp}
                      compactTime={extendedGroup.compact_time}
                      workflowStatus={workflowStatus}
                    />
                  </div>
                  {featureFlags[
                    'fe.resi.buyer_dashboard.enable_emd_instructions_banner'
                  ] && (
                    <InstructionsBanner
                      task={task}
                      resource={resource}
                      instructionActionSlot={slots?.instructionAction ?? null}
                    />
                  )}
                </React.Fragment>
              )
            })}

        {/* Inline prompt slot — rendered when a blocking task exists */}
        {blockingTask && slots?.inlinePrompt && (
          <div className={cx('inline-prompt-slot')}>
            {slots.inlinePrompt({ task: blockingTask, resource })}
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkflowDetails
